/**
 * GFS 太阳辐射 Symlog 灰度图解析器
 * 自动适配 8bit / 16bit PNG
 *
 * 16bit 规格:
 *   尺寸: 281×282, 深度16 (0~65535)
 *   第0行: pixel(0)=vmin编码, pixel(1)=vmax编码, 其余=32768
 *   第1~281行: 数据, 255×symlog(x)×256, dekod: gray=pixel16/256→symlogInverse
 *   经纬度: lon=70+x/280×70, lat=70-y/280×70
 *
 * 8bit 规格(兼容旧版):
 *   第0行前2像素灰度→线性映射[-400,2000]→min/max
 *   第1行起: val=gray/255×(max-min)+min
 */

const STORE_MIN = -400.0
const STORE_MAX = 2000.0
const EPS = 1e-6

/**
 * 读取IHDR获取图片信息
 */
function readIHDR(buffer) {
  const chunk = findChunk(buffer, 'IHDR')
  if (!chunk) throw new Error('PNG缺少IHDR块')
  const data = new DataView(buffer, chunk.offset, chunk.length)
  return {
    width: data.getUint32(0, false),
    height: data.getUint32(4, false),
    bitDepth: data.getUint8(8),
    colorType: data.getUint8(9)
  }
}

/**
 * 查找PNG中指定类型的chunk
 */
function findChunk(buffer, type) {
  let offset = 8
  while (offset < buffer.byteLength - 12) {
    const view = new DataView(buffer, offset)
    const len = view.getUint32(0, false)
    const chunkType = String.fromCharCode(
      view.getUint8(4), view.getUint8(5),
      view.getUint8(6), view.getUint8(7)
    )
    if (chunkType === type) {
      return { offset: offset + 8, length: len }
    }
    offset += 12 + len
  }
  return null
}

/**
 * 收集所有指定类型的chunk数据并拼接
 */
function collectChunks(buffer, type) {
  let offset = 8
  const chunks = []
  while (offset < buffer.byteLength - 12) {
    const view = new DataView(buffer, offset)
    const len = view.getUint32(0, false)
    const chunkType = String.fromCharCode(
      view.getUint8(4), view.getUint8(5),
      view.getUint8(6), view.getUint8(7)
    )
    if (chunkType === type) {
      chunks.push(buffer.slice(offset + 8, offset + 8 + len))
    }
    offset += 12 + len
  }
  const totalLen = chunks.reduce((s, c) => s + c.byteLength, 0)
  const result = new Uint8Array(totalLen)
  let pos = 0
  for (const c of chunks) {
    result.set(new Uint8Array(c), pos)
    pos += c.byteLength
  }
  return result
}

/**
 * Zlib解压
 */
async function zlibDecompress(data) {
  let offset = 2
  if (data.length > 2 && (data[1] & 0x20)) {
    offset += 4
  }
  const rawDeflate = data.slice(offset, data.length - 4)

  const compressedStream = new ReadableStream({
    start(controller) {
      controller.enqueue(rawDeflate)
      controller.close()
    }
  })

  const ds = compressedStream.pipeThrough(new DecompressionStream('deflate-raw'))
  const reader = ds.getReader()

  const chunks = []
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    chunks.push(value)
  }

  const totalLen = chunks.reduce((s, c) => s + c.length, 0)
  const result = new Uint8Array(totalLen)
  let pos = 0
  for (const c of chunks) {
    result.set(c, pos)
    pos += c.length
  }
  return result
}

/**
 * Paeth 预测器 (PNG 规范)
 */
function paethPredictor(a, b, c) {
  const p = a + b - c
  const pa = Math.abs(p - a)
  const pb = Math.abs(p - b)
  const pc = Math.abs(p - c)
  if (pa <= pb && pa <= pc) return a
  if (pb <= pc) return b
  return c
}

/**
 * PNG 字节级别 unfilter (mod 256)
 * @param {Uint8Array} cur - 当前行原始字节 (原地修改)
 * @param {Uint8Array} prev - 上一行重建后的字节
 * @param {number} filter - 滤波器类型 0-4
 * @param {number} width - 像素宽度
 * @param {number} bpp - 每像素字节数
 */
function unfilterBytes(cur, prev, filter, width, bpp) {
  const len = width * bpp
  switch (filter) {
    case 0: // None
      break
    case 1: // Sub
      for (let x = bpp; x < len; x++) {
        cur[x] = (cur[x] + cur[x - bpp]) & 0xFF
      }
      break
    case 2: // Up
      for (let x = 0; x < len; x++) {
        cur[x] = (cur[x] + prev[x]) & 0xFF
      }
      break
    case 3: // Average
      for (let x = 0; x < len; x++) {
        const a = x >= bpp ? cur[x - bpp] : 0
        const b = prev[x]
        cur[x] = (cur[x] + ((a + b) >> 1)) & 0xFF
      }
      break
    case 4: // Paeth
      for (let x = 0; x < len; x++) {
        const a = x >= bpp ? cur[x - bpp] : 0
        const b = prev[x]
        const c = x >= bpp ? prev[x - bpp] : 0
        cur[x] = (cur[x] + paethPredictor(a, b, c)) & 0xFF
      }
      break
  }
}

/**
 * 从PNG中提取像素数据 (自动适配8/16bit，含PNG解滤波)
 */
async function extractPixels(url) {
  const response = await fetch(url)
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const buffer = await response.arrayBuffer()

  const ihdr = readIHDR(buffer)

  const idatData = collectChunks(buffer, 'IDAT')
  const decompressed = await zlibDecompress(idatData)

  const { width, height, bitDepth } = ihdr
  const bpp = bitDepth === 16 ? 2 : 1
  const rowBytes = 1 + width * bpp

  const pixels = new Uint16Array(width * height)
  let prevRaw = new Uint8Array(width * bpp) // 上一行原始字节, 初始全零

  for (let y = 0; y < height; y++) {
    const rowStart = y * rowBytes
    const filter = decompressed[rowStart]
    // 复制当前行原始字节 (slice 会创建副本用于原地修改)
    const rowRaw = new Uint8Array(decompressed.slice(rowStart + 1, rowStart + rowBytes))

    unfilterBytes(rowRaw, prevRaw, filter, width, bpp)

    if (bpp === 2) {
      for (let x = 0; x < width; x++) {
        pixels[y * width + x] = (rowRaw[x * 2] << 8) | rowRaw[x * 2 + 1]
      }
    } else {
      for (let x = 0; x < width; x++) {
        pixels[y * width + x] = rowRaw[x]
      }
    }
    prevRaw = rowRaw
  }

  return { pixels, ihdr }
}

/**
 * symlog反向解码 (与后端 Python symlog_encode 完全对应)
 * 编码: ln(1+|x|) / ln(1+abs_max) → gray = 127 ± ratio×127/128
 * 解码: ratio → exp(ratio × ln(1+abs_max)) - 1.0
 */
function symlogInverse(gray, logAbsMax) {
  if (gray < 127) {
    const ratio = (127 - gray) / 127
    return -(Math.exp(ratio * logAbsMax) - 1.0)
  } else if (gray > 127) {
    const ratio = (gray - 127) / 128
    return Math.exp(ratio * logAbsMax) - 1.0
  } else {
    return 0
  }
}

/**
 * 经纬度 → 像素坐标
 */
function lonLatToPixel(lon, lat, w, h) {
  const x = Math.round(((lon - 70) / 70) * (w - 1))
  const y = Math.round(((70 - lat) / 70) * (h - 1))
  return { x: Math.max(0, Math.min(w - 1, x)), y: Math.max(0, Math.min(h - 1, y)) }
}

/**
 * 解析灰度PNG数据 (自动适配8bit/16bit)
 */
export async function parseGrayscalePNG(url) {
  const { pixels, ihdr } = await extractPixels(url)
  const { width, height, bitDepth } = ihdr
  const dataHeight = height - 1
  let vmin, vmax, absMax, logAbsMax
  const result = []

  if (bitDepth === 16) {
    vmin = pixels[0] / 65535 * (STORE_MAX - STORE_MIN) + STORE_MIN
    vmax = pixels[1] / 65535 * (STORE_MAX - STORE_MIN) + STORE_MIN
    absMax = Math.max(Math.abs(vmin), Math.abs(vmax))
    logAbsMax = Math.log(1.0 + absMax)

    for (let y = 1; y < height; y++) {
      const row = []
      for (let x = 0; x < width; x++) {
        const gray = pixels[y * width + x] / 256
        row.push(parseFloat(symlogInverse(gray, logAbsMax).toFixed(4)))
      }
      result.push(row)
    }
  } else {
    const rawMin = pixels[0]
    const rawMax = pixels[1]
    vmin = rawMin / 255 * (STORE_MAX - STORE_MIN) + STORE_MIN
    vmax = rawMax / 255 * (STORE_MAX - STORE_MIN) + STORE_MIN

    for (let y = 1; y < height; y++) {
      const row = []
      for (let x = 0; x < width; x++) {
        const gray = pixels[y * width + x]
        const val = (gray / 255) * (vmax - vmin) + vmin
        row.push(parseFloat(val.toFixed(4)))
      }
      result.push(row)
    }

    absMax = Math.max(Math.abs(vmin), Math.abs(vmax))
    logAbsMax = Math.log(1.0 + absMax)
  }

  return {
    min: vmin,
    max: vmax,
    absMax,
    logAbsMax,
    data: result,
    width,
    height: dataHeight,
    lonMin: 70, lonMax: 140, latMin: 0, latMax: 70,
    getValue(lon, lat) {
      const { x, y } = lonLatToPixel(lon, lat, width, dataHeight)
      if (y < 0 || y >= result.length) return null
      if (x < 0 || x >= result[y].length) return null
      return result[y][x]
    }
  }
}
